import React, { createContext, useEffect, useState, useContext } from 'react'
import { setItem, removeItem, getItem } from 'Utilities/LocalStorageUtility'
import { UserContext } from "Contexts/UserContext/UserContext";
import Retry from 'Utilities/Retry'
import { NOT_FOUND_COOKIE } from '../Constants/Cookies';
import SetCookie from 'Utilities/CookieUtilties/SetCookie'

let vehicleLocalStorageKey = 'customerVehicles'
const hasCustomerProjectsCookie = 'hasCustomerProjects'
export const VehicleContext = createContext()

const initialState = {
    currentTab: 'street',
    allYears: [],
    years: [],
    makes: [],
    models: [],
    raceTypes: [],
    prioritizedMakes: [],
    selectedVehicle: {
        yearId: null,
        makeId: null,
        makeDisplay: null,
        modelId: null,
        modelDisplay: null,
        raceTypeId: null,
        raceDisplay: null,
        shopUrl: null,
        searchUrl: null,
        projectId: null,
        vehicleBaseId: null,
        marketId: null,
        superMarketId: null,
        hydrated: false,
    },
    customerVehicles: null,
    reloadCurrentLocation: false, // this gets passed through from the navigation context to tell it when to reload the current page, TNG cant do this out right. Micro frontends can remove this
    hydrated: false,
    selectedFacetData: null,
}

export const VehicleStateProvider = ({ children, userGuid, updateUserGuid, services, vehicleState, reloadCurrentLocation, selectedFacetData }) => {
    const firstState = vehicleState || initialState;
    firstState.reloadCurrentLocation = reloadCurrentLocation
    firstState.selectedFacetData = selectedFacetData

    const [context, setContext] = useState(firstState)
    const [fitmentCookie, setFitmentCookie] = useState(null)
    const { context: userContext } = useContext(UserContext);

    const getVehicleLocalStorage = () => {
        return JSON.parse(getItem(vehicleLocalStorageKey))
    }

    const removeVehicleLocalStorage = () => {
        return removeItem(vehicleLocalStorageKey)
    }

    const UpdateYearMakeModelData = (ymmData) => {
        setContext(state => {
            return {
                ...state,
                years: state?.allYears?.length > 0 ? state.allYears : ymmData?.years,
                makes: ymmData?.makes,
                models: ymmData?.models,
                prioritizedMakes: ymmData?.prioritizedMakes,
                allYears: state.allYears?.length > 0 ? state.allYears : ymmData?.years,
                hydrated: true
            }
        })
    }
    const UpdateEnginesData = (engineData, type) => {
        let engineFamilies;
        let engines;
        if (type == 'engineFamilies') {
            engineFamilies = engineData;
        }
        else if (type == 'engines') {
            engines = engineData;
        }
        setContext(state => {
            return {
                ...state,
                engineFamilies: type == 'engines' ? state.engineFamilies : engineFamilies,
                engines: engines,
                hydrated: true
            }
        })
    }

    const UpdateRaceTypes = async (raceTypes, selectedRaceType, vehicles) => {
        try {
            var module = await Retry(() => import('Contexts/VehicleContext/Dynamic/UpdateRaceTypes'))
            var response = module.UpdateRaceTypes(context, raceTypes, selectedRaceType, vehicles, setFitmentCookie)
            setContext((state) => {
                if (!selectedFacetData?.selectedFacetOptions) {
                    setItem(vehicleLocalStorageKey, JSON.stringify({
                        customerProjects: response?.customerProjects,
                        userGuid: userGuid
                    }))
                    SetCookie(hasCustomerProjectsCookie, response?.customerProjects?.length > 0, 365)
                }

                return {
                    ...state,
                    ...response
                }
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    const SelectYearMakeModel = async (year, make, model, vehicles) => {
        try {
            var module = await Retry(() => import('Contexts/VehicleContext/Dynamic/SelectYearMakeModel'))
            var response = module.SelectYearMakeModel(context.models, context.makes, context.customerVehicles, year, make, model, vehicles)
            setContext((state) => {
                if (!selectedFacetData?.selectedFacetOptions) {
                    setItem(vehicleLocalStorageKey, JSON.stringify({
                        customerProjects: response?.customerProjectSource,
                        userGuid: userGuid
                    }))
                    SetCookie(hasCustomerProjectsCookie, response?.customerProjectSource?.length > 0, 365)
                }

                return {
                    ...state,
                    ...response
                }
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    const UpdateCustomerVehicles = (customerProjects) => {
        setContext((state) => {
            if (!selectedFacetData?.selectedFacetOptions) {
                setItem(vehicleLocalStorageKey, JSON.stringify({
                    customerProjects: customerProjects,
                    userGuid: userGuid
                }))
                SetCookie(hasCustomerProjectsCookie, customerProjects?.length > 0, 365)
            }

            return {
                ...state,
                customerVehicles: customerProjects,
                hydrated: true
            }
        })
    }

    // since the YMM dropdown functions are bound to the SMI object and reloadCurrentLocation is an argument, this needs to be a regular function
    const ExecuteGarageReload = reloadCurrentLocation => {
        setContext((state) => {
            return {
                ...state,
                reloadCurrentLocation
            }
        })
    }

    const SelectCustomerProject = async (projectId, shopUrl) => {
        try {
            var module = await Retry(() => import('Contexts/VehicleContext/Dynamic/SelectUserVehicle'))
            var response = module.SelectUserVehicle(context?.customerVehicles, context?.reloadCurrentLocation, projectId, shopUrl)

            if (response !== null) {
                setContext((state) => {
                    return {
                        ...state,
                        ...response
                    }
                })
            }
        }
        catch (err) {
            console.error(err)
        }
    }

    const ClearSelectedVehicle = async (redirectUrl) => {
        try {
            var module = await Retry(() => import('Contexts/VehicleContext/Dynamic/ClearSelectedVehicle'))
            var response = module.ClearSelectedVehicle(context?.reloadCurrentLocation, redirectUrl)
            setContext((state) => {
                return {
                    ...state,
                    ...response
                }
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    const SetVehicleFromCookie = async (setFitmentCookie, customerProjects, userGuid, url, updateUserGuid) => {
        try {
            var module = await Retry(() => import('Contexts/VehicleContext/Dynamic/SetVehicleFromCookie'))
            var response = await module.SetVehicleFromCookie(customerProjects, userGuid, url, UpdateCustomerVehicles, FindMatchingProjects)
            if (response?.cookie) {
                setFitmentCookie(response.cookie)
            }

            if (response?.guid) {
                updateUserGuid(response.guid)
            }
        }
        catch (err) {
            console.error(err)
        }
    }

    const FindMatchingProjects = (cookieToParse, customerProjects) => {
        try {
            if (!cookieToParse || cookieToParse == NOT_FOUND_COOKIE || !(customerProjects?.length > 0)) {
                return []
            }

            let safeCookieToParse = cookieToParse.split(';')[0]
            let cookieObject = JSON.parse(safeCookieToParse.split('=')[1])
            let cookieVehicleBaseId = cookieObject?.VehicleBaseId
            let cookieRaceTypeId = cookieObject?.RaceTypeId
            let matchingProjects = customerProjects?.filter(
                (project) => (project.vehicleBaseId !== null && project.vehicleBaseId === cookieVehicleBaseId) || (project.raceTypeId !== null && project.raceTypeId === cookieRaceTypeId)
            )

            return matchingProjects
        }
        catch (err) {
            console.error(err, customerProjects, cookieToParse)
            return []
        }
    }

    const setCurrentTab = tab => {
        setContext((state) => {
            return {
                ...state,
                currentTab: tab
            }
        })
    }

    useEffect(() => {
        async function fetchCustomerVehicles() {
            let response
            let locallyStoredCustomerProjects = getVehicleLocalStorage()

            if (locallyStoredCustomerProjects?.customerProjects?.length && locallyStoredCustomerProjects?.userGuid == userGuid && !selectedFacetData?.selectedFacetOptions) {
                response = {
                    customerProjects: locallyStoredCustomerProjects?.customerProjects
                }
            }
            else if (userContext?.isBot()) {
                response = null
            }
            else {
                var module = await Retry(() => import('Contexts/VehicleContext/CoreVehicleRequests'))
                response = await module.FetchUserVehicles(
                    userGuid,
                    context,
                    services?.customerVehicles?.url,
                    selectedFacetData
                )
            }

            if (response) {
                UpdateCustomerVehicles(response.customerProjects)
                SetCookie(hasCustomerProjectsCookie, response?.customerProjects?.length > 0, 365)
                SetVehicleFromCookie(setFitmentCookie, response.customerProjects, userGuid, services?.customerVehicles?.url, updateUserGuid)
            }
        }

        if (userGuid) {
            let locallyStoredCustomerProjects = getVehicleLocalStorage()
            if (locallyStoredCustomerProjects?.customerProjects?.length && !locallyStoredCustomerProjects.userGuid && !selectedFacetData?.selectedFacetOptions) {
                setItem(vehicleLocalStorageKey, JSON.stringify({
                    ...locallyStoredCustomerProjects,
                    userGuid
                }))
            }

            if (getVehicleLocalStorage()?.userGuid != userGuid) {
                removeVehicleLocalStorage()
            }

            fetchCustomerVehicles()
        }
    }, [userGuid])

    useEffect(() => {
        async function clearSelectedVehicle() {
            if (fitmentCookie && fitmentCookie !== NOT_FOUND_COOKIE && !userGuid) {
                await ClearSelectedVehicle()
            }

            //found cookie. parse it
            let matchingProjects = FindMatchingProjects(fitmentCookie, context?.customerVehicles)
            if (matchingProjects?.length > 0) {
                await SelectCustomerProject(matchingProjects[0].projectId)
            }
            else if (context?.hydrated){
                setContext(context => {
                    return {
                        ...context,
                        selectedVehicle: {
                            ...context?.selectedVehicle,
                            hydrated: fitmentCookie !== null
                        }
                    }
                })
            }
        }

        clearSelectedVehicle()

    }, [context?.customerVehicles, fitmentCookie, userGuid])

    return (
        <VehicleContext.Provider
            value={{
                context,
                UpdateYearMakeModelData,
                UpdateEnginesData,
                UpdateRaceTypes,
                SelectYearMakeModel,
                UpdateCustomerVehicles,
                ExecuteGarageReload,
                SelectCustomerProject,
                ClearSelectedVehicle,
                removeVehicleLocalStorage,
                setCurrentTab
            }}
        >
            {children}
        </VehicleContext.Provider>
    )
}
